import { GeckoColorScaleThreshold } from 'types'

import { DEFAULT_MISSING_COLOR } from '../heatmapCanvas/constants'
import {
  HEATMAP_BOTTOM_PADDING,
  HEATMAP_HORIZONTAL_PADDING,
  HEATMAP_LEFT_PADDING,
  HEATMAP_TOP_PADDING,
  LEGEND_HEIGHT_PX,
  LEGEND_WIDTH_PX,
} from './constants'
import {
  convertValuesToDisplayUnit,
  createColorScaleInfo,
  createDisplayBounds,
  cropValues,
} from './data'
import { createReportHeatmapDescriptor } from './descriptor'
import { getRightGutterWidth, renderReportOverlayCanvas } from './overlayLayout'
import { buildReportOverlayPrimitives } from './overlayPrimitives'
import { getCartesianPhysicalAspectRatio } from './physicalBinGeometry'
import {
  getCompassLabels,
  getCompassOrientationDegrees,
  hasFullPolarSweep,
  isPolarCircularHeatmap,
  polarToCanvasPoint,
  toCanvasAngleRadians,
  toDegrees,
} from './polarGeometry'
import { createNiceTicks, formatTick } from './ticks'
import type {
  ColorScaleInfo,
  CroppedValues,
  NormalizedBounds,
  PlotMetrics,
  ReportHeatmapBundle,
  ReportHeatmapRequest,
} from './types'
import { ReportOverlayMode as OverlayMode } from './types'
import { wrapCanvasText } from './utils'

const POLAR_PLOT_HORIZONTAL_PADDING = 72
const POLAR_PLOT_VERTICAL_PADDING = 72
const POLAR_COMPASS_TICK_INSET_PX = 4
const POLAR_COMPASS_TICK_LENGTH_PX = 8
const POLAR_COMPASS_LABEL_OFFSET_PX = 24

export async function renderReportHeatmapBundle(
  request: ReportHeatmapRequest
): Promise<ReportHeatmapBundle> {
  const descriptor = createReportHeatmapDescriptor(request)
  // Convert the full dataset to display units once and crop from the
  // converted buffer; elementwise conversion commutes with cropping. The
  // color scale must derive from the full dataset rather than the cropped
  // section values, so that every image rendered from the same CDF (the
  // component overview and each of its sections) shares one scale and legend.
  const displayRawValues = convertValuesToDisplayUnit(
    request.rawValues,
    request.cdf.data.params.measurement_unit
  )
  const croppedValues = cropValues(
    request.cdf,
    request.bounds,
    displayRawValues,
    request.rawValuesXBinCount
  )
  const displayValues = croppedValues.values
  const colorScale = createColorScaleInfo(
    request.cdf,
    request.colorThreshold as GeckoColorScaleThreshold | undefined,
    displayRawValues
  )
  const heatmapCanvas = renderHeatmapCanvas(
    request,
    croppedValues,
    displayValues,
    colorScale
  )
  const legendCanvas = renderLegendCanvas(request, colorScale)

  return {
    heatmapBlob: await canvasToBlob(heatmapCanvas),
    legendBlob: await canvasToBlob(legendCanvas),
    descriptor,
  }
}

function renderHeatmapCanvas(
  request: ReportHeatmapRequest,
  croppedValues: CroppedValues,
  displayValues: Float32Array,
  colorScale: ColorScaleInfo
) {
  const canvas = createHtmlCanvas()
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Failed to create report heatmap canvas context')
  }

  const renderPolarCircular = isPolarCircularHeatmap(
    request.componentModel,
    croppedValues.bounds
  )
  const rightGutterWidth = renderPolarCircular
    ? 0
    : getRightGutterWidth(ctx, request, croppedValues.bounds)

  let plotMetrics: PlotMetrics
  if (renderPolarCircular) {
    canvas.width = request.outputWidthPx
    const plotDiameter = Math.max(
      48,
      request.outputWidthPx - POLAR_PLOT_HORIZONTAL_PADDING * 2
    )
    canvas.height = plotDiameter + POLAR_PLOT_VERTICAL_PADDING * 2
    plotMetrics = createPolarPlotMetrics(canvas, plotDiameter)
  } else {
    const plotWidth = Math.max(
      1,
      request.outputWidthPx -
        HEATMAP_LEFT_PADDING -
        HEATMAP_HORIZONTAL_PADDING -
        rightGutterWidth
    )
    const aspectRatio = getCartesianPhysicalAspectRatio(
      request.componentModel,
      request.cdf,
      croppedValues
    )
    const plotHeight = Math.max(48, Math.round(plotWidth * aspectRatio))

    canvas.width = request.outputWidthPx
    canvas.height = HEATMAP_TOP_PADDING + plotHeight + HEATMAP_BOTTOM_PADDING
    plotMetrics = createPlotMetrics(canvas, rightGutterWidth)
  }

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  if (request.includeHeatmap !== false) {
    if (renderPolarCircular) {
      drawPolarCircularCells(
        ctx,
        croppedValues,
        displayValues,
        colorScale,
        plotMetrics
      )
    } else {
      drawCartesianCells(
        ctx,
        croppedValues,
        displayValues,
        colorScale,
        plotMetrics
      )
    }
  }

  if (
    !renderPolarCircular &&
    request.includeOverlay &&
    request.overlayMode === OverlayMode.Embedded
  ) {
    const overlayCanvas = renderOverlayCanvas(
      request,
      croppedValues.bounds,
      plotMetrics
    )
    if (overlayCanvas) {
      ctx.drawImage(overlayCanvas, 0, 0)
    }
  }

  if (request.includeAxes) {
    if (renderPolarCircular) {
      drawPolarCircularAxes(ctx, plotMetrics, request, croppedValues.bounds)
    } else {
      drawAxes(ctx, plotMetrics, croppedValues.bounds)
    }
  }

  return canvas
}

function drawCartesianCells(
  ctx: CanvasRenderingContext2D,
  croppedValues: CroppedValues,
  displayValues: Float32Array,
  colorScale: ColorScaleInfo,
  plotMetrics: PlotMetrics
) {
  const cellWidth = plotMetrics.width / croppedValues.xBinCount
  const cellHeight = plotMetrics.height / croppedValues.yBinCount
  const drawCellWidth =
    cellWidth >= 1
      ? Math.max(1, Math.ceil(cellWidth))
      : Math.max(cellWidth, Number.EPSILON)
  const drawCellHeight =
    cellHeight >= 1
      ? Math.max(1, Math.ceil(cellHeight))
      : Math.max(cellHeight, Number.EPSILON)

  for (let y = 0; y < croppedValues.yBinCount; y++) {
    for (let x = 0; x < croppedValues.xBinCount; x++) {
      const index = y * croppedValues.xBinCount + x
      const value = displayValues[index]
      ctx.fillStyle = Number.isFinite(value)
        ? colorScale.colorScale.getColorForValue(value)
        : DEFAULT_MISSING_COLOR
      ctx.fillRect(
        plotMetrics.left + x * cellWidth,
        plotMetrics.top + (croppedValues.yBinCount - 1 - y) * cellHeight,
        drawCellWidth,
        drawCellHeight
      )
    }
  }
}

function drawPolarCircularCells(
  ctx: CanvasRenderingContext2D,
  croppedValues: CroppedValues,
  displayValues: Float32Array,
  colorScale: ColorScaleInfo,
  plotMetrics: PlotMetrics
) {
  const centerX = plotMetrics.left + plotMetrics.width / 2
  const centerY = plotMetrics.top + plotMetrics.height / 2
  const plotRadius = plotMetrics.width / 2
  const xStart = toDegrees(
    croppedValues.bounds.rect.x.min.toNumber(croppedValues.bounds.rect.x.unit),
    croppedValues.bounds.rect.x.unit
  )
  const xEnd = toDegrees(
    croppedValues.bounds.rect.x.max.toNumber(croppedValues.bounds.rect.x.unit),
    croppedValues.bounds.rect.x.unit
  )
  const yStart = croppedValues.bounds.rect.y.min.toNumber(
    croppedValues.bounds.rect.y.unit
  )
  const yEnd = croppedValues.bounds.rect.y.max.toNumber(
    croppedValues.bounds.rect.y.unit
  )
  const angleSpan = xEnd - xStart
  const radiusSpan = Math.max(yEnd - yStart, Number.EPSILON)

  for (let y = 0; y < croppedValues.yBinCount; y++) {
    const innerRadius =
      (plotRadius *
        (yStart + (y * radiusSpan) / croppedValues.yBinCount - yStart)) /
      radiusSpan
    const outerRadius =
      (plotRadius *
        (yStart + ((y + 1) * radiusSpan) / croppedValues.yBinCount - yStart)) /
      radiusSpan

    for (let x = 0; x < croppedValues.xBinCount; x++) {
      const index = y * croppedValues.xBinCount + x
      const value = displayValues[index]
      const angleStart = xStart + (x * angleSpan) / croppedValues.xBinCount
      const angleEnd = xStart + ((x + 1) * angleSpan) / croppedValues.xBinCount

      ctx.fillStyle = Number.isFinite(value)
        ? colorScale.colorScale.getColorForValue(value)
        : DEFAULT_MISSING_COLOR

      fillPolarSector(
        ctx,
        centerX,
        centerY,
        innerRadius,
        outerRadius,
        angleStart,
        angleEnd
      )
    }
  }
}

function renderLegendCanvas(
  request: ReportHeatmapRequest,
  colorScale: ColorScaleInfo
) {
  const canvas = createHtmlCanvas()
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Failed to create report legend canvas context')
  }

  canvas.width = LEGEND_WIDTH_PX
  canvas.height = LEGEND_HEIGHT_PX

  ctx.fillStyle = request.background === 'white' ? '#ffffff' : '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const gradientX = 24
  const titleX = gradientX
  const titleY = 16
  const titleLineHeight = 13
  const titleLines = wrapCanvasText(
    ctx,
    request.legendTitle ?? request.cdf.data.params.view.name,
    canvas.width - gradientX - 6
  )
  const gradientY =
    titleY + Math.max(titleLines.length, 1) * titleLineHeight + 10
  const gradientWidth = 30
  const gradientHeight = canvas.height - gradientY - 24
  const gradient = ctx.createLinearGradient(
    0,
    gradientY + gradientHeight,
    0,
    gradientY
  )

  for (let step = 0; step <= 1; step += 0.02) {
    const value = colorScale.min + (colorScale.max - colorScale.min) * step
    gradient.addColorStop(step, colorScale.colorScale.getColorForValue(value))
  }

  ctx.fillStyle = gradient
  ctx.fillRect(gradientX, gradientY, gradientWidth, gradientHeight)
  ctx.strokeStyle = '#1f1f1f'
  ctx.strokeRect(gradientX, gradientY, gradientWidth, gradientHeight)

  ctx.fillStyle = '#1f1f1f'
  ctx.font = 'bold 12px Arial'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  titleLines.forEach((line, index) => {
    ctx.fillText(line, titleX, titleY + index * titleLineHeight)
  })
  ctx.font = '11px Arial'
  ctx.fillText(colorScale.measurementUnit.name, gradientX, canvas.height - 8)

  const ticks = [
    { label: formatTick(colorScale.max), value: colorScale.max },
    { label: formatTick(colorScale.mid), value: colorScale.mid },
    { label: formatTick(colorScale.min), value: colorScale.min },
  ]

  ticks.forEach((tick) => {
    const ratio =
      colorScale.max === colorScale.min
        ? 0.5
        : (tick.value - colorScale.min) / (colorScale.max - colorScale.min)
    const y = gradientY + gradientHeight - ratio * gradientHeight

    ctx.beginPath()
    ctx.moveTo(gradientX + gradientWidth, y)
    ctx.lineTo(gradientX + gradientWidth + 8, y)
    ctx.stroke()
    ctx.fillText(tick.label, gradientX + gradientWidth + 14, y + 4)
  })

  return canvas
}

function drawAxes(
  ctx: CanvasRenderingContext2D,
  plotMetrics: PlotMetrics,
  bounds: NormalizedBounds
) {
  const displayBounds = createDisplayBounds(bounds)
  const xStart = displayBounds.x.start
  const xEnd = displayBounds.x.end
  const yStart = displayBounds.y.start
  const yEnd = displayBounds.y.end
  const plotLeft = plotMetrics.left
  const plotTop = plotMetrics.top
  const plotRight = plotMetrics.right
  const plotBottom = plotMetrics.bottom
  const plotWidth = plotMetrics.width
  const plotHeight = plotMetrics.height

  ctx.strokeStyle = '#1f1f1f'
  ctx.fillStyle = '#1f1f1f'
  ctx.lineWidth = 1
  ctx.font = '12px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'

  ctx.beginPath()
  ctx.moveTo(plotLeft, plotBottom)
  ctx.lineTo(plotRight, plotBottom)
  ctx.lineTo(plotRight, plotTop)
  ctx.stroke()

  const xTicks = createNiceTicks(xStart, xEnd, 6)
  xTicks.forEach((tick) => {
    const ratio = xEnd === xStart ? 0 : (tick - xStart) / (xEnd - xStart)
    const x = plotLeft + ratio * plotWidth
    ctx.beginPath()
    ctx.moveTo(x, plotBottom)
    ctx.lineTo(x, plotBottom + 6)
    ctx.stroke()
    ctx.fillText(formatTick(tick), x, plotBottom + 10)
  })

  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  const yTicks = createNiceTicks(yStart, yEnd, 5)
  yTicks.forEach((tick) => {
    const ratio = yEnd === yStart ? 0 : (tick - yStart) / (yEnd - yStart)
    const y = plotBottom - ratio * plotHeight
    ctx.beginPath()
    ctx.moveTo(plotLeft - 6, y)
    ctx.lineTo(plotLeft, y)
    ctx.stroke()
    ctx.fillText(formatTick(tick), plotLeft - 10, y)
  })
}

function drawPolarCircularAxes(
  ctx: CanvasRenderingContext2D,
  plotMetrics: PlotMetrics,
  request: ReportHeatmapRequest,
  bounds: NormalizedBounds
) {
  const centerX = plotMetrics.left + plotMetrics.width / 2
  const centerY = plotMetrics.top + plotMetrics.height / 2
  const outerRadius = plotMetrics.width / 2

  ctx.strokeStyle = '#1f1f1f'
  ctx.fillStyle = '#1f1f1f'
  ctx.lineWidth = 2
  ctx.font = '12px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  ctx.beginPath()
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2)
  ctx.stroke()

  if (!hasFullPolarSweep(bounds)) {
    return
  }

  const compassLabels = getCompassLabels(
    getCompassOrientationDegrees(request.componentModel),
    10
  )

  compassLabels.forEach(({ label, positionDegrees }) => {
    const tickStart = polarToCanvasPoint(
      centerX,
      centerY,
      outerRadius + POLAR_COMPASS_TICK_INSET_PX,
      positionDegrees
    )
    const tickEnd = polarToCanvasPoint(
      centerX,
      centerY,
      outerRadius + POLAR_COMPASS_TICK_INSET_PX + POLAR_COMPASS_TICK_LENGTH_PX,
      positionDegrees
    )

    ctx.beginPath()
    ctx.moveTo(tickStart.x, tickStart.y)
    ctx.lineTo(tickEnd.x, tickEnd.y)
    ctx.stroke()

    const labelPoint = polarToCanvasPoint(
      centerX,
      centerY,
      outerRadius +
        POLAR_COMPASS_TICK_INSET_PX +
        POLAR_COMPASS_TICK_LENGTH_PX +
        POLAR_COMPASS_LABEL_OFFSET_PX,
      positionDegrees
    )
    ctx.font = /^[NESW]$/.test(label) ? 'bold 13px Arial' : '12px Arial'
    ctx.fillText(label, labelPoint.x, labelPoint.y)
  })
}

function createPlotMetrics(
  canvas: HTMLCanvasElement,
  rightGutterWidth: number
): PlotMetrics {
  const left = HEATMAP_LEFT_PADDING
  const top = HEATMAP_TOP_PADDING
  const gutterRight = canvas.width - HEATMAP_HORIZONTAL_PADDING
  const gutterLeft = gutterRight - rightGutterWidth
  const right = gutterLeft
  const bottom = canvas.height - HEATMAP_BOTTOM_PADDING

  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    gutterLeft,
    gutterRight,
  }
}

function createPolarPlotMetrics(
  canvas: HTMLCanvasElement,
  plotDiameter: number
): PlotMetrics {
  const left = Math.round((canvas.width - plotDiameter) / 2)
  const top = Math.round((canvas.height - plotDiameter) / 2)
  const right = left + plotDiameter
  const bottom = top + plotDiameter

  return {
    left,
    top,
    right,
    bottom,
    width: plotDiameter,
    height: plotDiameter,
    gutterLeft: right,
    gutterRight: right,
  }
}

function fillPolarSector(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  innerRadius: number,
  outerRadius: number,
  angleStartDegrees: number,
  angleEndDegrees: number
) {
  const outerStart = toCanvasAngleRadians(angleStartDegrees)
  const outerEnd = toCanvasAngleRadians(angleEndDegrees)

  ctx.beginPath()
  if (innerRadius <= 0.5) {
    ctx.moveTo(centerX, centerY)
    ctx.arc(centerX, centerY, outerRadius, outerStart, outerEnd, true)
  } else {
    ctx.arc(centerX, centerY, outerRadius, outerStart, outerEnd, true)
    ctx.arc(centerX, centerY, innerRadius, outerEnd, outerStart, false)
  }
  ctx.closePath()
  ctx.fill()
}

function renderOverlayCanvas(
  request: ReportHeatmapRequest,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  const primitives = buildReportOverlayPrimitives(request, bounds)
  if (
    primitives.linePrimitives.length === 0 &&
    primitives.shapePrimitives.length === 0 &&
    primitives.labelPrimitives.length === 0
  ) {
    return undefined
  }

  const canvas = createHtmlCanvas()
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to create report overlay canvas context')
  }

  canvas.width = request.outputWidthPx
  canvas.height =
    HEATMAP_TOP_PADDING + plotMetrics.height + HEATMAP_BOTTOM_PADDING

  renderReportOverlayCanvas(ctx, primitives, bounds, plotMetrics)
  return canvas
}

function createHtmlCanvas() {
  if (typeof document === 'undefined') {
    throw new Error('Report heatmap rendering requires a browser canvas')
  }

  return document.createElement('canvas')
}

async function canvasToBlob(canvas: HTMLCanvasElement) {
  if (canvas.toBlob) {
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png')
    )
    if (blob) {
      return blob
    }
  }

  const dataUrl = canvas.toDataURL('image/png')
  const response = await fetch(dataUrl)
  return response.blob()
}
